import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { FaLinkedin, FaInstagram, FaTwitter, FaWhatsapp, FaGlobe, FaGithub, FaFacebook, FaTiktok, FaYoutube, FaSnapchat, FaPinterest, FaTelegram, FaSpotify, FaDiscord } from 'react-icons/fa';
import { SiThreads } from 'react-icons/si';
import { Plus, X, Eye, Image, FileText, Video, Share2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';
import GalleryManager from '@/components/GalleryManager';
import VideoManager from '@/components/VideoManager';
import PDFManager from '@/components/PDFManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_PLATFORMS = [
  { name: 'WhatsApp', icon: FaWhatsapp, color: '#25D366' },
  { name: 'Instagram', icon: FaInstagram, color: '#E4405F' },
  { name: 'Facebook', icon: FaFacebook, color: '#1877F2' },
  { name: 'X (Twitter)', icon: FaTwitter, color: '#000000' },
  { name: 'Threads', icon: SiThreads, color: '#000000' },
  { name: 'LinkedIn', icon: FaLinkedin, color: '#0A66C2' },
  { name: 'TikTok', icon: FaTiktok, color: '#000000' },
  { name: 'YouTube', icon: FaYoutube, color: '#FF0000' },
  { name: 'Telegram', icon: FaTelegram, color: '#0088CC' },
  { name: 'Snapchat', icon: FaSnapchat, color: '#FFFC00' },
  { name: 'Pinterest', icon: FaPinterest, color: '#E60023' },
  { name: 'GitHub', icon: FaGithub, color: '#333333' },
  { name: 'Discord', icon: FaDiscord, color: '#5865F2' },
  { name: 'Spotify', icon: FaSpotify, color: '#1DB954' },
  { name: 'Website', icon: FaGlobe, color: '#6B7280' }
];

const ProfileEditor = ({ profile: initialProfile, onProfileUpdate }) => {
  const [profile, setProfile] = useState(initialProfile);
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    job_title: '',
    company: '',
    bio: '',
    phone: '',
    email: '',
    website: '',
    profile_picture: '',
    company_logo: '',
    gallery: [],
    videos: [],
    pdfs: []
  });
  const [socialLinks, setSocialLinks] = useState([]);
  const [newLink, setNewLink] = useState({ platform: 'WhatsApp', url: '' });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');

  const fetchSocialLinks = useCallback(async () => {
    if (!profile) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/profiles/${profile.profile_id}/social-links`);
      setSocialLinks(response.data);
    } catch (error) {
      console.error('Failed to fetch social links:', error);
    }
  }, [profile]);

  useEffect(() => {
    if (profile) {
      setFormData({
        username: profile.username || '',
        name: profile.name || '',
        job_title: profile.job_title || '',
        company: profile.company || '',
        bio: profile.bio || '',
        phone: profile.phone || '',
        email: profile.email || '',
        website: profile.website || '',
        profile_picture: profile.profile_picture || '',
        company_logo: profile.company_logo || '',
        gallery: profile.gallery || [],
        videos: profile.videos || [],
        pdfs: profile.pdfs || []
      });
      fetchSocialLinks();
    }
  }, [profile, fetchSocialLinks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (profile) {
        await axios.put(`${BACKEND_URL}/api/profiles/me`, formData, { withCredentials: true });
        toast.success('Profile updated successfully!');
      } else {
        await axios.post(`${BACKEND_URL}/api/profiles`, formData, { withCredentials: true });
        toast.success('Profile created successfully!');
      }
      onProfileUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSocialLink = async () => {
    if (!newLink.url) {
      toast.error('Please enter a URL');
      return;
    }
    
    if (!profile) {
      toast.error('Please create your profile first to add social links');
      return;
    }

    let formattedUrl = newLink.url.trim();
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = 'https://' + formattedUrl;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/profiles/${profile.profile_id}/social-links`,
        { ...newLink, url: formattedUrl },
        { withCredentials: true }
      );
      setSocialLinks([...socialLinks, response.data]);
      setNewLink({ platform: 'WhatsApp', url: '' });
      toast.success('Social link added!');
    } catch (error) {
      toast.error('Failed to add social link');
    }
  };

  const handleDeleteSocialLink = async (linkId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/social-links/${linkId}`, { withCredentials: true });
      setSocialLinks(socialLinks.filter(link => link.link_id !== linkId));
      toast.success('Social link removed!');
    } catch (error) {
      toast.error('Failed to delete social link');
    }
  };

  const profileUrl = profile ? `${window.location.origin}/p/${profile.username}` : '';

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: null },
    { id: 'social', label: 'Social Links', icon: Share2 },
    { id: 'gallery', label: 'Gallery', icon: Image },
    { id: 'videos', label: 'Videos', icon: Video },
    { id: 'documents', label: 'Documents', icon: FileText },
  ];

  return (
    <div className="max-w-7xl mx-auto" data-testid="profile-editor">
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold text-[#0A0A0A]">Profile Editor</h1>
        <p className="text-[#525252] mt-1">Create and manage your digital business card</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6 bg-white p-2 rounded-xl">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              activeTab === tab.id 
                ? 'bg-[#0A0A0A] text-white' 
                : 'text-[#525252] hover:text-[#0A0A0A] hover:bg-[#F5F1EA]'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            {tab.icon && <tab.icon className="w-4 h-4" />}
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - 2 columns */}
        <div className="lg:col-span-2 space-y-6">
          
          {/* Basic Information Tab */}
          {activeTab === 'basic' && (
            <>
              <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4">Basic Information</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label className="text-[#0A0A0A]">Username *</Label>
                    <Input value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} placeholder="johndoe" disabled={!!profile} className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] mt-2" data-testid="username-input" required />
                    {!profile && <p className="text-xs text-[#525252] mt-1">This will be your profile URL: soldigital.com/p/username</p>}
                  </div>

                  <div>
                    <Label className="text-[#0A0A0A]">Full Name *</Label>
                    <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Doe" className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] mt-2" data-testid="name-input" required />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#0A0A0A]">Job Title</Label>
                      <Input value={formData.job_title} onChange={(e) => setFormData({ ...formData, job_title: e.target.value })} placeholder="Software Engineer" className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] mt-2" data-testid="job-title-input" />
                    </div>
                    <div>
                      <Label className="text-[#0A0A0A]">Company</Label>
                      <Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} placeholder="Acme Inc." className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] mt-2" data-testid="company-input" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-[#0A0A0A]">Bio</Label>
                    <Textarea value={formData.bio} onChange={(e) => setFormData({ ...formData, bio: e.target.value })} placeholder="Tell people about yourself..." className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] mt-2 min-h-[100px]" data-testid="bio-input" />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#0A0A0A]">Email</Label>
                      <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@example.com" className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] mt-2" data-testid="email-input" />
                    </div>
                    <div>
                      <Label className="text-[#0A0A0A]">Phone</Label>
                      <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+91 98765 43210" className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] mt-2" data-testid="phone-input" />
                    </div>
                  </div>

                  <div>
                    <Label className="text-[#0A0A0A]">Website</Label>
                    <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} placeholder="https://example.com" className="bg-[#F5F1EA] border-[#0A0A0A]/10 text-[#0A0A0A] mt-2" data-testid="website-input" />
                  </div>

                  <Button type="submit" disabled={loading} className="w-full bg-[#0A0A0A] text-white rounded-full h-12" data-testid="save-profile-button">
                    {loading ? 'Saving...' : (profile ? 'Update Profile' : 'Create Profile')}
                  </Button>
                </form>
              </div>

              <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4">Profile Images</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <ImageUpload imageType="profile" currentImage={formData.profile_picture} onImageUploaded={(url) => setFormData({ ...formData, profile_picture: url })} label="Profile Photo" aspectRatio="square" />
                    <p className="text-xs text-[#525252] mt-2">Displayed on left side of card</p>
                  </div>
                  <div>
                    <ImageUpload imageType="logo" currentImage={formData.company_logo} onImageUploaded={(url) => setFormData({ ...formData, company_logo: url })} label="Company Logo" aspectRatio="square" />
                    <p className="text-xs text-[#525252] mt-2">Displayed at bottom-right</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Social Links Tab */}
          {activeTab === 'social' && (
            <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4">Social Media Links</h2>
              <p className="text-[#525252] mb-6">Add your social media profiles to help people connect with you.</p>
              
              {!profile && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
                  <p className="text-yellow-400 text-sm">Please create your profile first in the "Basic Info" tab to add social links.</p>
                </div>
              )}

              {/* Add New Link */}
              <div className="bg-[#F5F1EA] rounded-xl p-4 mb-6">
                <Label className="text-[#0A0A0A] mb-3 block">Add New Social Link</Label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <select 
                    value={newLink.platform} 
                    onChange={(e) => setNewLink({ ...newLink, platform: e.target.value })} 
                    className="bg-zinc-800 border border-[#0A0A0A]/10 text-[#0A0A0A] rounded-lg px-4 py-3 min-w-[160px]" 
                    data-testid="social-platform-select"
                  >
                    {SOCIAL_PLATFORMS.map(p => (
                      <option key={p.name} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                  <Input 
                    value={newLink.url} 
                    onChange={(e) => setNewLink({ ...newLink, url: e.target.value })} 
                    placeholder="Enter your profile URL" 
                    className="bg-zinc-800 border-[#0A0A0A]/10 text-[#0A0A0A] flex-1" 
                    data-testid="social-url-input" 
                  />
                  <Button onClick={handleAddSocialLink} className="bg-[#0A0A0A] px-6" data-testid="add-social-link-button">
                    <Plus className="w-4 h-4 mr-2" /> Add
                  </Button>
                </div>
                <p className="text-xs text-[#525252] mt-2">Paste your full profile URL (https:// will be added automatically if missing)</p>
              </div>

              {/* Existing Links */}
              {socialLinks.length > 0 ? (
                <div className="space-y-3">
                  <Label className="text-[#0A0A0A] mb-2 block">Your Social Links ({socialLinks.length})</Label>
                  {socialLinks.map((link) => {
                    const platform = SOCIAL_PLATFORMS.find(p => p.name === link.platform);
                    const Icon = platform?.icon || FaGlobe;
                    return (
                      <div key={link.link_id} className="flex items-center gap-3 bg-[#F5F1EA] rounded-xl p-4" data-testid={`social-link-${link.platform.toLowerCase()}`}>
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${platform?.color}20` }}>
                          <Icon className="w-5 h-5" style={{ color: platform?.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[#0A0A0A] font-medium">{link.platform}</p>
                          <p className="text-sm text-[#525252] truncate">{link.url}</p>
                        </div>
                        <Button onClick={() => handleDeleteSocialLink(link.link_id)} variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 border border-dashed border-[#0A0A0A]/10 rounded-xl">
                  <Share2 className="w-10 h-10 text-[#525252] mx-auto mb-3" />
                  <p className="text-[#525252]">No social links added yet</p>
                  <p className="text-sm text-[#525252] mt-1">Add WhatsApp, Instagram, Facebook, and more!</p>
                </div>
              )}

              {/* Available Platforms Preview */}
              <div className="mt-6 pt-6 border-t border-[#0A0A0A]/10">
                <Label className="text-[#0A0A0A] mb-3 block">Available Platforms</Label>
                <div className="flex flex-wrap gap-3">
                  {SOCIAL_PLATFORMS.map((platform) => {
                    const Icon = platform.icon;
                    return (
                      <div key={platform.name} className="flex items-center gap-2 bg-[#F5F1EA] rounded-full px-3 py-1.5">
                        <Icon className="w-4 h-4" style={{ color: platform.color }} />
                        <span className="text-xs text-[#525252]">{platform.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Gallery Tab */}
          {activeTab === 'gallery' && (
            <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
              <GalleryManager gallery={formData.gallery} onGalleryUpdate={(gallery) => setFormData({ ...formData, gallery })} />
              
              {formData.gallery.length > 0 && (
                <div className="mt-4">
                  <Button onClick={handleSubmit} disabled={loading} className="w-full bg-[#0A0A0A] text-white rounded-full h-12">
                    {loading ? 'Saving...' : 'Save Gallery Changes'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Videos Tab */}
          {activeTab === 'videos' && (
            <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
              <VideoManager videos={formData.videos} onVideosUpdate={(videos) => setFormData({ ...formData, videos })} />
              
              {formData.videos.length > 0 && (
                <div className="mt-4">
                  <Button onClick={handleSubmit} disabled={loading} className="w-full bg-[#0A0A0A] text-white rounded-full h-12">
                    {loading ? 'Saving...' : 'Save Video Changes'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
              <PDFManager pdfs={formData.pdfs} onPDFsUpdate={(pdfs) => setFormData({ ...formData, pdfs })} />
              
              {formData.pdfs.length > 0 && (
                <div className="mt-4">
                  <Button onClick={handleSubmit} disabled={loading} className="w-full bg-[#0A0A0A] text-white rounded-full h-12">
                    {loading ? 'Saving...' : 'Save Document Changes'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar - Preview & QR Code */}
        <div className="space-y-6">
          {profile && (
            <>
              <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4">Quick Actions</h2>
                <div className="space-y-3">
                  <a href={profileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-[#B8935A]/15 hover:bg-[#B8935A]/20 border border-[#B8935A]/20 rounded-lg p-4 transition-all" data-testid="view-profile-link">
                    <Eye className="w-5 h-5 text-[#B8935A]" />
                    <div>
                      <p className="text-[#0A0A0A] font-medium">View Public Profile</p>
                      <p className="text-xs text-[#525252] truncate">{profileUrl}</p>
                    </div>
                  </a>
                </div>
              </div>

              <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
                <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4">QR Code</h2>
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-xl" data-testid="qr-code">
                    <QRCodeSVG value={profileUrl} size={180} />
                  </div>
                  <p className="text-xs text-[#525252] mt-3 text-center">Share this QR code to connect instantly</p>
                </div>
              </div>
            </>
          )}

          {!profile && (
            <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-[#0A0A0A] mb-4">Getting Started</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#B8935A]/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-[#B8935A] font-bold">1</span>
                  </div>
                  <div>
                    <p className="text-[#0A0A0A] font-medium">Fill Basic Info</p>
                    <p className="text-sm text-[#525252]">Add your name, title, and contact details</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#F5F1EA] flex items-center justify-center flex-shrink-0">
                    <span className="text-[#525252] font-bold">2</span>
                  </div>
                  <div>
                    <p className="text-[#525252] font-medium">Add Social Links</p>
                    <p className="text-sm text-[#525252]">Connect your social profiles</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#F5F1EA] flex items-center justify-center flex-shrink-0">
                    <span className="text-[#525252] font-bold">3</span>
                  </div>
                  <div>
                    <p className="text-[#525252] font-medium">Add Media</p>
                    <p className="text-sm text-[#525252]">Upload gallery photos, videos & documents</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Profile Stats */}
          {profile && (
            <div className="bg-white border border-[#0A0A0A]/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-[#0A0A0A] mb-4">Profile Summary</h2>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-[#525252]">Social Links</span>
                  <span className="text-[#0A0A0A] font-medium">{socialLinks.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#525252]">Gallery Photos</span>
                  <span className="text-[#0A0A0A] font-medium">{formData.gallery.length}/6</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#525252]">Videos</span>
                  <span className="text-[#0A0A0A] font-medium">{formData.videos.length}/5</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#525252]">Documents</span>
                  <span className="text-[#0A0A0A] font-medium">{formData.pdfs.length}/5</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileEditor;
